import React, { useState, useLayoutEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { NAVER_CLIENT_ID, NAVER_CALLBACK_URL } from "./NaverConfig";
import "./Sidebar.css";

function Header() {
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useLayoutEffect(() => {
    if (!isHome) {
      setIsScrolled(true); // 다른 페이지는 항상 흰색 배경
      return;
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome]);

  const menuItems = [
    { name: "홈", path: "/" },
    { name: "시설안내", path: "/about" },
    { name: "게시판", path: "/services" },
    { name: "SNS", path: "/sns" },
    { name: "스케줄", path: "/schedule" },
    { name: "문의", path: "/inquiry" },
    { name: "오시는길", path: "/location" },
  ];

  const isLoggedIn = sessionStorage.getItem("isLoggedIn") === "true";
  const userName = sessionStorage.getItem("userName");

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/");
  };

  const loginUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${NAVER_CLIENT_ID}&redirect_uri=${encodeURIComponent(
    NAVER_CALLBACK_URL
  )}&state=1234`;

  const handleNaverLogin = () => {
    window.location.href = loginUrl;
  };

  return (
    <>
      {showSidebar && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden"
          onClick={() => setShowSidebar(false)}
        />
      )}
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white shadow-md"
            : isHome
            ? "bg-transparent"
            : "bg-white"
        }`}>
        <nav className="max-w-screen-xl mx-auto flex items-center justify-between px-2 py-4">
          {/* 햄버거 버튼 - 모바일 전용 */}
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            className="md:hidden text-white">
            <svg
              className={`w-6 h-6 transition-colors duration-300 ${
                isScrolled ? "text-black" : "text-white"
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <div
            className={`text-xl font-bold cursor-pointer transition-colors duration-300 ${
              isScrolled ? "text-black" : "text-white"
            }`}
            onClick={() => navigate("/")}>
            <span className="hidden sm:block">아이엠플러스교육심리센터</span>
          </div>
          <ul
            className={`flex md:flex gap-6 text-sm tracking-wide overflow-x-auto whitespace-nowrap px-2 sm:px-4 scrollbar-hide`}>
            {menuItems.map((item) => (
              <li
                key={item.path}
                className={`shrink-0 ${item.name === "홈" ? "sm:hidden" : ""}`}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `transition font-semibold font-score ${
                      isActive
                        ? isScrolled
                          ? "text-blue-300 border-b-2 border-blue-100"
                          : "text-white border-b-2 border-white"
                        : isScrolled
                        ? "text-gray-700 hover:text-blue-300"
                        : "text-white hover:text-blue-100"
                    }`
                  }>
                  {item.name}
                </NavLink>
              </li>
            ))}
          </ul>
          <div
            className={`flex space-x-4 text-sm font-semibold items-center transition-colors duration-300 ${
              isScrolled ? "text-gray-700" : "text-white"
            }`}>
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="hidden sm:inline hover:underline hover:text-blue-300">
              전체보기
            </button>
            {isLoggedIn ? (
              <>
                <span className="text-blue-500 font-bold">{userName} 님</span>
                <button
                  onClick={handleLogout}
                  className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-600 font-semibold px-2 py-1">
                  로그아웃
                </button>
              </>
            ) : (
              <button
                onClick={handleNaverLogin}
                className="hidden md:inline-block text-xs bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2 rounded">
                네이버 아이디로 로그인
              </button>
            )}
          </div>
        </nav>
      </header>

      <div className={`sidebar ${showSidebar ? "show" : ""}`}>
        <div className="flex justify-between items-center mb-6">
          <span className="title">아이엠플러스교육심리센터</span>
          <button
            onClick={() => setShowSidebar(false)}
            className="text-gray-500 hover:text-black">
            ✕
          </button>
        </div>
        {/* ✅ 모바일 전용 네이버 로그인 버튼 */}
        <div className="block md:hidden mb-4">
          <button
            onClick={handleNaverLogin}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2">
            네이버 로그인
          </button>
        </div>
        <ul className="space-y-4 text-sm tracking-wide">
          {menuItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                onClick={() => setShowSidebar(false)}
                className={({ isActive }) =>
                  `transition font-semibold font-score ${
                    isActive
                      ? isScrolled
                        ? "text-blue-300 border-b-2 border-blue-100"
                        : "text-white border-b-2 border-white"
                      : isScrolled
                      ? "text-gray-700 hover:text-blue-400"
                      : "text-white hover:text-blue-100"
                  }`
                }>
                {item.name}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

export default Header;
